import { isEqual, pickBy, debounce } from 'lodash'
import * as React from 'react'
import gql from 'graphql-tag'
import { useQuery } from '@apollo/react-hooks'
import { FastField, Field, FieldArray, Form, Formik, FormikConsumer } from 'formik'
import styled from 'styled-components'
import { FormattedMessage, injectIntl, InjectedIntlProps } from 'react-intl'
import * as ReactModal from 'react-modal'
import { Query } from 'react-apollo'
import Spin from 'web-makeup/dist/Spin'
import Select from 'web-makeup/dist/Select'
import Box from 'web-makeup/dist/Box'
import H5 from 'web-makeup/dist/H5'
import Input from 'web-makeup/dist/Input'
import Button from 'web-makeup/dist/Button'
import { parse as qp } from 'query-string'

import { client as apolloClient } from '../ApolloProvider'
import Budget from '../Budget'
import { GetQuoteForm, FindCity, AllCountries, QuoteInput } from '../../typings/types'
import PurchaseTitle from '../PurchaseTitle'
import OnFormikValueChange from '../OnFormikValueChange'
import { SECONDARY_COLOR } from '../../style'

import { BoxHeader, H5SubText } from './../Purchase'
import PurchaseShootTime from './../PurchaseShootTime'
import PurchaseShootInspiration from './../PurchaseShootInspiration'

const { TextArea } = Input
const Option = Select.Option

export const PaddedBox = styled(Box as any)`
  padding: 1rem;
  margin: 0;
  margin-bottom: 1.5rem;

  @media (min-width: 48rem) {
    max-width: 45rem;
    padding: 2rem;
  }

  @media (min-width: 64rem) {
    max-width: 61rem;
  }

  > ${BoxHeader}:not(:nth-of-type(1)) {
    margin-top: 1.5rem;
  }
`

const TitleBox = styled(PaddedBox as any)`
  padding: 1.5rem;
  margin-bottom: 1.5rem;

  @media (min-width: 48rem) {
    padding: 1.5rem;
    margin-top: 2rem;

    .ant-input-suffix button {
      border-radius: 0;
    }

    > .ant-btn {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
`

const StyledForm = styled(Form)`
  margin-bottom: 3rem;

  @media (min-width: 48rem) {
    margin-bottom: 6rem;
    margin-top: 0.375rem;
  }

  @media (min-width: 64rem) {
    margin-bottom: 0;
  }
  .ant-select + .ant-select {
    margin: 1rem 0 0;
  }
`

const StyledSelect = styled(Select as any)`
  width: 100%;
`

const Next = styled(Button as any)`
  height: auto;
  font-size: 1rem;
  margin-left: 1.5rem;
  float: right;
  &&,
  &&:hover,
  &&:focus {
    background-color: ${SECONDARY_COLOR};
    border-color: ${SECONDARY_COLOR};
    padding: 0 2.375rem;
  }
`

const TocContainer = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 61rem;

  @media (max-width: 40rem) {
    padding: 0 1rem;
  }
`

const Toc = styled.div`
  input {
    margin-right: 0.3rem;
  }
  a {
    color: ${SECONDARY_COLOR};
  }
`

const ModalButton: any = styled(Button as any)`
  height: auto;
  font-size: 1rem;
`

const ModalContent = styled.div`
  padding: 1rem;

  button {
    display: block;
    margin: 1rem auto 0;
  }
`

const ModalHeader = styled.div`
  padding: 1rem 1rem 0.5rem;
  border-bottom: 0.063rem #d1d1d1 solid;
`

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    padding: 0,
  },
  overlay: {
    zIndex: 1000,
    backgroundColor: 'rgba(51, 51, 51, 0.5)',
  },
}

export const GET_COUNTRIES = gql`
  query allCountries {
    allCountries {
      uuid
      name
    }
  }
`
export const GET_CITIES = gql`
  query FindCity($countryUuid: ID, $query: String) {
    findCity(countryUuid: $countryUuid, searchTerm: $query) {
      cities {
        uuid
        name
        default
        country {
          uuid
        }
      }
    }
  }
`

export interface OnChangeProps {
  prevValues: QuoteInput
  nextValues: QuoteInput
  keysOfChangedValues: Array<keyof QuoteInput>
}

interface Props {
  onSubmit: (values, actions) => void
  onChange: (props: OnChangeProps) => void
  isFetching: boolean
  initialValues: GetQuoteForm.Variables['quote']
  availableOptions: GetQuoteForm.GetQuoteForm
}

interface FormEffectState {
  values: GetQuoteForm.Variables['quote']
}

export const handleFormEffectChange = (onChange: Props['onChange']) => (
  prevFormikState: FormEffectState,
  nextFormikState: FormEffectState
) => {
  const { values: nextValues } = nextFormikState
  const { values: prevValues } = prevFormikState

  // This is plural/array because I am not certain if React will
  // batch updates causing multiple keys to be changed at once.
  const keysOfChangedValues: Array<keyof GetQuoteForm.Variables['quote']> = Object.keys(
    pickBy(nextValues, (value, key) => !isEqual(prevValues[key], value))
  ) as Array<keyof GetQuoteForm.Variables['quote']>

  // Without this, `onChange` will be triggered twice because Formik always does double-render.
  // https://github.com/jaredpalmer/formik/issues/390
  if (keysOfChangedValues.length === 0) {
    return
  }

  return onChange({ prevValues, nextValues, keysOfChangedValues })
}

export const getCategoryUuid = (searchCategory: string) => {
  if (searchCategory) {
    const { data } = useQuery(GET_CITIES, {
      variables: { query: searchCategory },
      client: apolloClient,
    })
    if (data && data.findCity && data.findCity.cities && data.findCity.cities.length > 0) {
      const defaultCity = data.findCity.cities.find(city => city.default)
      const firstCity = data.findCity.cities[0]
      if (firstCity) {
        return { dCountry: firstCity.country, dCity: firstCity }
      }
      if (defaultCity) {
        return { dCountry: defaultCity.country, dCity: defaultCity }
      }
    }
  }
  return { dCountry: [], dCity: [] }
}

export const PurchaseOrder: React.FunctionComponent<Props & InjectedIntlProps> = ({
  isFetching,
  initialValues,
  availableOptions,
  onSubmit,
  onChange,
  intl,
}) => {
  const searchCategory =
    'category' in qp(window.location.search) ? qp(window.location.search).category : ''
  const { dCountry, dCity } = getCategoryUuid(searchCategory)
  const [countryUuid, setCountryUuid] = React.useState<string>(dCountry.uuid)
  const [cityUuid, setCityUuid] = React.useState<string | []>(dCity.uuid)
  const [cityQuery, setCityQuery] = React.useState<string>()
  const setCityQueryDebounced = React.useCallback(debounce(setCityQuery, 500), [])
  const [checked, setChecked] = React.useState(true)
  const [showModal, setShowModal] = React.useState(false)

  const handleOpenModal = () => setShowModal(true)
  const handleCloseModal = () => setShowModal(false)

  return (
    <>
      <Formik<QuoteInput>
        // To allow for one-way data flow, the response from BE (or really just `props`)
        // must always be the single source of truth when data is not in sync.
        enableReinitialize
        initialValues={{
          ...initialValues,
          custom: true,
          budget: 1000,
          title: intl.formatMessage({ id: 'purchase.project.new' }),
        }}
        onSubmit={onSubmit}
      >
        {({ values, isSubmitting }) => (
          <StyledForm>
            <FormikConsumer>
              {formik => (
                <OnFormikValueChange formik={formik} onChange={handleFormEffectChange(onChange)} />
              )}
            </FormikConsumer>
            <TitleBox>
              <FastField name="title">
                {({ field }) => (
                  <PurchaseTitle
                    fieldName={field.name}
                    color={SECONDARY_COLOR}
                    data-testid="purchase_title"
                  />
                )}
              </FastField>
            </TitleBox>
            <PaddedBox>
              <Field name="projectDueAt">
                {({ field: { name }, form }) => {
                  return (
                    <PurchaseShootTime
                      data-testid="purchase_dueDate"
                      title={<FormattedMessage id="purchase.project.due_date.title" />}
                      description={<FormattedMessage id="purchase.project.due_date.description" />}
                      loading={isFetching}
                      onChange={value => form.setFieldValue(name, value)}
                      value={values[name]}
                      selectedLocation={availableOptions.quote.videoProjectTemplate.slug}
                      selectedTime={availableOptions.quote.projectDueAt}
                      color={SECONDARY_COLOR}
                      iconNow="time-profiles/icons/micro-violet.png"
                      iconSchedule="time-profiles/icons/full-violet.png"
                    />
                  )
                }}
              </Field>
            </PaddedBox>
            <PaddedBox>
              <BoxHeader>
                <H5>
                  <FormattedMessage id="purchase.shoot.location.title" />
                </H5>
                <H5SubText>
                  <FormattedMessage id="purchase.shoot.location.description" />
                </H5SubText>
              </BoxHeader>
              <Field name="countryUuid" data-testid="purchase_country">
                {({ field: { name }, form }) => (
                  <Query<AllCountries.Query, AllCountries.Variables>
                    query={GET_COUNTRIES}
                    errorPolicy="all"
                  >
                    {({ loading, error, data: queryData }) => {
                      if (loading || (searchCategory && dCountry.uuid === undefined)) {
                        return <Spin size="small" />
                      }
                      const data = (queryData && queryData.allCountries) || []
                      const blackListedCountry = [
                        'Iran',
                        'Cuba',
                        'North Korea',
                        'Venezuela',
                        'Ukraine',
                        'Syria',
                      ]
                      const filteredData = data.filter(obj => {
                        return !blackListedCountry.some(v => obj.name === v)
                      })
                      return (
                        <StyledSelect
                          data-testid="purchase_country_select"
                          showSearch
                          placeholder="Select a country"
                          defaultValue={dCountry.uuid}
                          onChange={value => {
                            setCountryUuid(value)
                            setCityUuid([])
                            form.setFieldValue(name, value)
                          }}
                          filterOption={(input, option) =>
                            option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {filteredData.map(country => (
                            <Option key={country.uuid} value={country.uuid}>
                              {country.name}
                            </Option>
                          ))}
                        </StyledSelect>
                      )
                    }}
                  </Query>
                )}
              </Field>

              <Field name="cityUuid" data-testid="purchase_city">
                {({ field: { name }, form }) => (
                  <Query<FindCity.Query, FindCity.Variables>
                    query={GET_CITIES}
                    variables={{ countryUuid, query: cityQuery }}
                    errorPolicy="all"
                  >
                    {({ data: queryData, loading }) => {
                      const data =
                        (queryData && queryData.findCity && queryData.findCity.cities) || []
                      if (!cityUuid && !data.find(city => city.uuid === dCity.uuid)) {
                        data.push({
                          name: dCity.name,
                          uuid: dCity.uuid,
                          default: true,
                          country: {
                            uuid: countryUuid,
                          },
                        })
                      }
                      return (
                        <StyledSelect
                          data-testid="purchase_city_select"
                          notFoundContent={
                            loading ? (
                              <Spin size="small" />
                            ) : (
                              <FormattedMessage id="purchase.shoot.location.not_found" />
                            )
                          }
                          showSearch
                          value={cityUuid ? cityUuid : dCity.uuid}
                          disabled={!countryUuid && !dCountry.uuid ? true : false}
                          onSearch={setCityQueryDebounced}
                          onChange={value => {
                            setCityUuid(value)
                            form.setFieldValue(name, value)
                          }}
                          placeholder="Select a city"
                          filterOption={(input, option) =>
                            option.props.children.toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {data.map(city => (
                            <Option key={city.uuid} value={city.uuid}>
                              {city.name}
                            </Option>
                          ))}
                        </StyledSelect>
                      )
                    }}
                  </Query>
                )}
              </Field>
            </PaddedBox>
            <PaddedBox>
              <Field name="budget" data-testid="purchase_budget">
                {({ field: { name }, form }) => {
                  return (
                    <Budget
                      value={values[name]}
                      onChange={value => form.setFieldValue(name, value)}
                    />
                  )
                }}
              </Field>
            </PaddedBox>
            <PaddedBox>
              <FastField name="description">
                {({ field }) => (
                  <React.Fragment>
                    <BoxHeader>
                      <H5>
                        <FormattedMessage
                          id="purchase.project.overview.title"
                          data-testid="purchase_title"
                        />
                      </H5>
                      <H5SubText>
                        <FormattedMessage id="purchase.project.overview.description" />
                      </H5SubText>
                    </BoxHeader>
                    <TextArea
                      {...field}
                      autosize={{ minRows: 4 }}
                      disabled={isFetching}
                      data-testid="purchase_description"
                    />
                  </React.Fragment>
                )}
              </FastField>
            </PaddedBox>
            <PaddedBox>
              <FieldArray name="inspirationVideoUrls">
                {({ push, remove }) => (
                  <React.Fragment>
                    <PurchaseShootInspiration
                      data-testid="purchase_description"
                      loading={isFetching}
                      onAdd={push}
                      onRemove={remove}
                      values={values.inspirationVideoUrls}
                      title={<FormattedMessage id="purchase.project.inspirations.title" />}
                      description={
                        <FormattedMessage id="purchase.project.inspirations.description" />
                      }
                      color={SECONDARY_COLOR}
                    />
                  </React.Fragment>
                )}
              </FieldArray>
            </PaddedBox>
            <TocContainer>
              <Toc>
                <label>
                  <input
                    data-testid="toc-checkbox"
                    type="checkbox"
                    defaultChecked={checked}
                    onChange={() => (checked ? setChecked(false) : setChecked(true))}
                  />
                  <FormattedMessage id="purchase.toc.part1" />
                  <a target="_blank" href="https://90seconds.com/brand-terms/">
                    <FormattedMessage id="purchase.toc.part2" />
                  </a>
                  <FormattedMessage id="purchase.toc.and" />
                  <a target="_blank" href="https://90seconds.com/privacy/">
                    <FormattedMessage id="purchase.toc.part3" />
                  </a>
                  .
                </label>
              </Toc>

              <Next
                onClick={e => {
                  checked ? onSubmit(values, e) : handleOpenModal()
                }}
                data-testid="submit-button"
                type="primary"
                loading={isSubmitting}
                disabled={isFetching}
                className={'submitButton'}
              >
                <FormattedMessage id="purchase.shoot.steps.next" />
              </Next>
            </TocContainer>
          </StyledForm>
        )}
      </Formik>

      <ReactModal
        isOpen={showModal}
        style={modalStyles}
        onRequestClose={handleCloseModal}
        data-testid="purchase_modal"
      >
        <ModalHeader>
          <H5>
            <FormattedMessage id="react.modal.title" />
          </H5>
        </ModalHeader>
        <ModalContent>
          <FormattedMessage id="react.modal.message" />
          <ModalButton onClick={() => handleCloseModal()} type="primary">
            <FormattedMessage id="react.modal.button" />
          </ModalButton>
        </ModalContent>
      </ReactModal>
    </>
  )
}

export default injectIntl(PurchaseOrder)



// WEBPACK FOOTER //
// ./src/components/PurchaseOrder/PurchaseOrder.tsx